/**
 * Admin → Excel/CSV import hooks (rates + plans).
 *
 * Both endpoints take a multipart upload with a `file` field. Preview
 * returns per-row validation; Import commits and returns counts + the
 * same error list. The frontend renders preview → confirm → import.
 *
 * `postMultipart` routes through the shared api client so it inherits the
 * 401 -> refresh -> retry handling; a raw fetch would 401 with
 * `invalid_token` when a slow import wizard outlives the access token.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

async function postMultipart<T>(path: string, formData: FormData): Promise<T> {
  // Pass the FormData via `options.body` (not the JSON `body` arg) so the
  // browser owns the multipart boundary; the client only injects Authorization
  // and handles the token refresh + single retry on a 401.
  return api.post<T>(path, undefined, { body: formData });
}

// ----- Shared shapes --------------------------------------------------------

export interface ImportRowError {
  row: number;
  message: string;
}

export interface ImportResult {
  total_rows: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: ImportRowError[];
}

// ----- Rates ----------------------------------------------------------------

export interface RatePreviewSample {
  name?: string;
  prefix: string;
  message_type?: string;
  provider_id: number;
  buy_cost: number;
  sell_price: number;
  priority: number;
  is_active: boolean;
  notes?: string;
}

export interface RateImportPreview {
  detected_kind: string;
  headers: string[];
  total_rows: number;
  valid_rows: number;
  errors: ImportRowError[];
  sample: RatePreviewSample[];
}

// Rate import is per rate card now (the new model has many cards per
// reseller). Pass the cardId at hook construction time so each page
// knows which card to upsert into.
export function usePreviewImportRates(cardId: number) {
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return postMultipart<RateImportPreview>(
        `/v1/admin/rate-cards/${cardId}/rates/import-preview`,
        fd,
      );
    },
  });
}

export function useRunImportRates(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return postMultipart<ImportResult>(
        `/v1/admin/rate-cards/${cardId}/rates/import`,
        fd,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-cards', cardId, 'rates'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-cards'] });
      // The /admin/rates global view caches its own list — refresh it
      // too so a freshly-imported rate appears without a manual reload.
      void qc.invalidateQueries({ queryKey: ['admin', 'rates'] });
    },
  });
}

// ----- Plans ----------------------------------------------------------------

export interface PlanPreviewSample {
  name: string;
  tagline?: string;
  monthly_fee: number;
  included_sms?: number | null;
  overage_per_sms: number;
  features: string[];
  is_popular: boolean;
  color: string;
  is_active: boolean;
}

export interface PlanImportPreview {
  detected_kind: string;
  headers: string[];
  total_rows: number;
  valid_rows: number;
  errors: ImportRowError[];
  sample: PlanPreviewSample[];
}

export function usePreviewImportPlans() {
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return postMultipart<PlanImportPreview>('/v1/admin/plans/import-preview', fd);
    },
  });
}

export function useRunImportPlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return postMultipart<ImportResult>('/v1/admin/plans/import', fd);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'plans', 'subscribers'] });
    },
  });
}
